import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

interface CompetitorEntry {
  name: string;
  firstSeenAt: string;
  lastSeenAt: string;
  lastAbsentAt?: string;
  seenCount: number;
  missedCount: number;
  presentOnLastCheck: boolean;
  wasThereAtStart: boolean;
  status: "active" | "removed" | "intermittent" | "new";
}

interface DetectionRow {
  siteId: number;
  platform: string;
  firstDetectedAt: string;
  lastDetectedAt: string;
  detectionCount: number;
  suggestionDismissedAt?: string;
  competitors: CompetitorEntry[];
  pageUrls: string[];
}

const STATUS_COLORS: Record<string, string> = {
  active:      "bg-green-100 text-green-800",
  removed:     "bg-red-100 text-red-800",
  intermittent:"bg-yellow-100 text-yellow-800",
  new:         "bg-blue-100 text-blue-800",
  unknown:     "bg-slate-100 text-slate-600",
};

const PLATFORM_LABELS: Record<string, string> = {
  shopify:     "Shopify",
  bigcommerce: "BigCommerce",
  woocommerce: "WooCommerce",
  magento:     "Magento",
  square:      "Square",
  unknown:     "Unknown",
};

function fmtDate(d?: string) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function DetectionReport() {
  const api = useApi();
  const [rows, setRows] = useState<DetectionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");

  useEffect(() => {
    async function load() {
      try {
        const res = await api.admin.get("SiteDetection/Report");
        if (res.ok) {
          const data = await res.json();
          setRows(Array.isArray(data) ? data : []);
        }
      } catch {
        // non-critical
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [api.admin]);

  const filtered = rows.filter(r => {
    const matchesPlatform = platformFilter === "all" || r.platform === platformFilter;
    const matchesSearch   = !filter || String(r.siteId).includes(filter) ||
      r.competitors.some(c => c.name.toLowerCase().includes(filter.toLowerCase()));
    return matchesPlatform && matchesSearch;
  });

  const platforms = ["all", "shopify", "bigcommerce", "woocommerce", "magento", "square", "unknown"];

  if (loading) {
    return <div className="p-6 text-slate-500">Loading detection report…</div>;
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-4">
        <p className="text-slate-500 text-sm mb-4">
          Platform and competitor data collected from embed.js across all customer sites.
          One row per site — data accumulates over every widget page load.
        </p>
        <div className="flex gap-3 flex-wrap">
          <input
            type="text"
            placeholder="Search by site ID or competitor…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm w-64"
          />
          <select
            value={platformFilter}
            onChange={e => setPlatformFilter(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm"
          >
            {platforms.map(p => (
              <option key={p} value={p}>{p === "all" ? "All Platforms" : PLATFORM_LABELS[p] ?? p}</option>
            ))}
          </select>
          <span className="text-slate-400 text-sm self-center">{filtered.length} sites</span>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[70vh] overflow-y-auto border rounded-lg">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="sticky top-0 bg-slate-50 border-b">
            <tr>
              <th className="px-3 py-2 font-medium text-slate-600">Site ID</th>
              <th className="px-3 py-2 font-medium text-slate-600">Platform</th>
              <th className="px-3 py-2 font-medium text-slate-600">First Seen</th>
              <th className="px-3 py-2 font-medium text-slate-600">Last Seen</th>
              <th className="px-3 py-2 font-medium text-slate-600">Checks</th>
              <th className="px-3 py-2 font-medium text-slate-600">Competitors Detected</th>
              <th className="px-3 py-2 font-medium text-slate-600">Pages</th>
              <th className="px-3 py-2 font-medium text-slate-600">Dismissed?</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map(row => (
              <tr key={row.siteId} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-slate-700">{row.siteId}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[row.platform] ?? STATUS_COLORS.unknown}`}>
                    {PLATFORM_LABELS[row.platform] ?? row.platform}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-500">{fmtDate(row.firstDetectedAt)}</td>
                <td className="px-3 py-2 text-slate-500">{fmtDate(row.lastDetectedAt)}</td>
                <td className="px-3 py-2 text-slate-500">{row.detectionCount}</td>
                <td className="px-3 py-2">
                  {row.competitors.length === 0 ? (
                    <span className="text-slate-400 text-xs">None</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {row.competitors.map(c => (
                        <span
                          key={c.name}
                          title={`First seen: ${fmtDate(c.firstSeenAt)} | Last seen: ${fmtDate(c.lastSeenAt)} | Was there at start: ${c.wasThereAtStart ? "Yes" : "No"} | Seen ${c.seenCount}x`}
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium cursor-help ${STATUS_COLORS[c.status] ?? STATUS_COLORS.unknown}`}
                        >
                          {c.name}
                          {c.wasThereAtStart && <span className="ml-1 opacity-70">★</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  {row.pageUrls.length > 0 ? (
                    <span className="text-slate-400 text-xs truncate max-w-[150px] inline-block" title={row.pageUrls.join("\n")}>
                      {row.pageUrls[0]}
                    </span>
                  ) : (
                    <span className="text-slate-300 text-xs">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-500 text-xs">
                  {row.suggestionDismissedAt ? fmtDate(row.suggestionDismissedAt) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-10 text-slate-400">No detection data yet. Data populates as customers load the widget.</div>
        )}
      </div>

      <p className="mt-3 text-xs text-slate-400">
        ★ = competitor was present when the site first installed Velaro (pre-existing tool).
        Hover a competitor badge to see full history details.
        Status: <span className="text-green-700">active</span> = on last check, <span className="text-red-700">removed</span> = gone 3+ checks, <span className="text-yellow-700">intermittent</span> = sometimes, <span className="text-blue-700">new</span> = seen once.
      </p>
    </div>
  );
}
